import { type Common } from '../common.js';
import {
  type CreateValues,
  type FindAllOptions,
  type Organization,
} from '../models/organization/index.js';
import { type PaginatedQueryOptions, type PaginationResponse } from '../models/pagination/index.js';
import { type SamlSetting } from '../models/samlSetting.js';
import { type User } from '../models/user.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/organizations';
  }

  async create(toAdd: CreateValues): Promise<Organization> {
    const url = this.common.constructPath(this.baseEndpoint);
    const { organization } = await this.common.postJSON(url, toAdd);
    return organization;
  }

  async findAll(): Promise<Organization[]> {
    const url = this.common.constructPath(this.baseEndpoint);
    const { organizations } = await this.common.getJSON(url);
    return organizations;
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<Organization>> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<Organization> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { organization } = await this.common.getJSON(url);
    return organization;
  }

  async findUsersByOrganizationId(id: string): Promise<User[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/users`);
    const { users } = await this.common.getJSON(url);
    return users;
  }

  async update(id: string, values: CreateValues): Promise<Organization> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { organization } = await this.common.patchJSON(url, values);
    return organization;
  }

  async remove(id: string): Promise<number> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async setSsoEnabled(id: string, ssoEnabled: boolean): Promise<Organization> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/sso`);
    const { organization } = await this.common.patchJSON(url, {
      ssoEnabled,
    });
    return organization;
  }

  async findSamlSetting(organizationId: string): Promise<SamlSetting> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${organizationId}/saml-setting`);
    const { samlSetting } = await this.common.getJSON(url);
    return samlSetting;
  }
}
